import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import Head from 'next/head'
import { useRouter } from 'next/router'
import { IBM_Plex_Mono, Instrument_Serif, Inter_Tight } from 'next/font/google'
import { SmoothScroll } from '@/components/motion/smooth-scroll'
import { TransitionProvider } from '@/components/motion/transition-provider'
import { Atmosphere } from '@/components/generals/atmosphere'
import { Header } from '@/components/generals/header'

const sans = Inter_Tight({
    subsets: ['latin']
})

const mono = IBM_Plex_Mono({
    subsets: ['latin'],
    weight: ['400', '500']
})

const serif = Instrument_Serif({
    subsets: ['latin'],
    weight: '400',
    style: ['normal', 'italic']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${sans.style.fontFamily};--font-app-serif:${serif.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()
    const pageKey = router.asPath.split('#')[0]

    return (
        <div className='h-full font-sans'>
            <Head>
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
                <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
            </Head>

            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:bg-foreground focus:px-4 focus:py-3 focus:text-background'
            >
                Skip to content
            </a>

            <SmoothScroll>
                <TransitionProvider>
                    <Header />
                    <Component key={pageKey} {...pageProps} />
                    <Atmosphere />
                </TransitionProvider>
            </SmoothScroll>
        </div>
    )
}

export default App
